import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Building2, Check, ChevronDown, ChevronRight, Layers, Loader2, Search } from 'lucide-react';
import type { ZoneType } from '@/api/zones';
import { ALL_BRANCHES_PARAM, GROUP_PARAM_PREFIX, parseGroupParam, useAppActions, useAppState } from '@/app/appContext';
import { useBranchGroupMode, type MyBranchGroup } from '@/hooks/useBranchScope';
import { useZonesList } from '@/hooks/useCurrentZone';
import { RETAIL_ZONE_TYPES } from '@/lib/zoneComponents';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Tooltip } from '@/components/ui/tooltip';

interface Zone {
  id: string | number;
  humanreadable_name?: string;
  type?: ZoneType;
  is_archived?: boolean;
}

/** «Архив» — филиал больше не анализируется, но его данные доступны (AI14). */
function ArchivedBadge() {
  const { t } = useT();
  return (
    <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {t('branchSelector.archived')}
    </span>
  );
}

// Types shown to non-superusers by default — shared with the all-branches
// boards so both surfaces enumerate the same branches.
const RETAIL_TYPES: readonly ZoneType[] = RETAIL_ZONE_TYPES as readonly ZoneType[];

/** `block` — крупный блок филиала вверху мобильного меню: иконка, подпись
 *  «Филиал», название и стрелка вбок, как смена проекта в Notion/Figma
 *  (Алексей 17.09, AI12.6). `onPicked` — закрыть меню после выбора. */

// Поиск по-русски тоже (Алексей 25.10 #2317: «Остоженка» должна находить Surf Coffee Ostozhenka).
// Обе строки переводим в латиницу; если не совпало — сравниваем «скелеты» из согласных:
// «Индастриал» и «Industrial» пишутся по-разному, а согласные у них одни.
const RU_LAT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};
function toLat(s: string): string {
  return s.toLowerCase().replace(/дж/g, 'j').replace(/кс/g, 'x').replace(/[а-яё]/g, (ch) => RU_LAT[ch] ?? ch);
}
const skeleton = (s: string) => s.replace(/[aeiouyhw\s\-–—.,'"«»]/g, '').replace(/(.)\1+/g, '$1');
function nameMatches(name: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const n = name.toLowerCase();
  if (n.includes(q)) return true;
  const nl = toLat(n); const ql = toLat(q);
  if (nl.includes(ql) || nl.replace(/y/g, 'i').includes(ql.replace(/y/g, 'i'))) return true;   // «джолли» → Jolly
  const qs = skeleton(ql);
  return qs.length >= 3 && skeleton(nl).includes(qs);
}

export function BranchSelector({ className, variant = 'default', onPicked }: {
  className?: string;
  variant?: 'default' | 'block';
  onPicked?: () => void;
}) {
  const { t } = useT();
  const { setZone, setAllBranches, setBranchGroup, setCamera, setFrame, setSelectedCamera } = useAppActions();
  const { zone } = useAppState();
  const selectedZoneId = zone.selectedZoneId;
  const allBranches = zone.allBranches;
  const branchGroupId = zone.branchGroupId;
  // Группы филиалов (Алексей 22.10, #1969): выбрал группу — «Все филиалы» только по ней.
  const { groups, showGroups, showAllBranches } = useBranchGroupMode();
  const activeGroup = branchGroupId ? groups.find(g => String(g.id) === branchGroupId) ?? null : null;

  // Тот же список и те же настройки, что у useCurrentZone: с кешем прошлой
  // сессии и быстрым повтором. Свой голый useSWR без кеша давал красную ошибку
  // при первом неудачном запросе на старте приложения.
  const { data: zones, isLoading, error } = useZonesList();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showService, setShowService] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [hlIndex, setHlIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const sortedZones = useMemo(
    // Архивные — в конце списка.
    () => (zones ?? []).slice().sort((a, b) =>
      Number(!!a.is_archived) - Number(!!b.is_archived)
      || (a.humanreadable_name || '').localeCompare(b.humanreadable_name || '')),
    [zones]
  );

  // Архивные зоны ведут себя как служебные (Алексей 21.09, AI112): по умолчанию
  // свёрнуты, а под списком появляется «показать архивные зоны». Список зон
  // приходит уже отфильтрованным по доступу, поэтому «есть архивная в списке» и
  // есть то самое «есть доступ хотя бы к одной архивной» из просьбы.
  const hasArchived = useMemo(() => sortedZones.some(z => z.is_archived), [sortedZones]);
  // Если у человека ВСЕ зоны архивные, прятать нечего — иначе он открыл бы
  // пустой список и решил, что доступ отобрали.
  const onlyArchived = hasArchived && sortedZones.every(z => z.is_archived);

  // Non-superusers only see retail branch types. Superusers see all by default,
  // with an optional toggle to hide service zones.
  const visibleZones = useMemo(() => {
    const byType = isSuperuser && showService
      ? sortedZones
      : sortedZones.filter(z => !z.type || RETAIL_TYPES.includes(z.type));
    if (showArchived || onlyArchived) return byType;
    // Выбранную зону не прячем, даже если она архивная: иначе в списке не видно,
    // что именно выбрано, и галочка уезжает в никуда.
    return byType.filter(z => !z.is_archived || String(z.id) === selectedZoneId);
  }, [sortedZones, isSuperuser, showService, showArchived, onlyArchived, selectedZoneId]);

  const selectedZone = sortedZones.find(z => String(z.id) === selectedZoneId) ?? null;

  // Guard against SWR revalidation races: if zones is briefly empty during a
  // background refetch, the find above returns null and the trigger would flash
  // to the placeholder text. Cache the last resolved name so the trigger stays
  // stable as long as a zone is selected in context.
  const lastDisplayNameRef = useRef<string | null>(null);
  if (selectedZone?.humanreadable_name) {
    lastDisplayNameRef.current = selectedZone.humanreadable_name;
  }
  // All-branches mode reads as a real selection («Все филиалы»), not as the
  // empty-state placeholder — per Alexey's follow-up to the original spec.
  const triggerLabel = branchGroupId
    ? (activeGroup?.name ?? t('branchSelector.group'))
    : allBranches
    ? t('branchSelector.allBranches')
    : selectedZone?.humanreadable_name
      ?? (selectedZoneId ? (lastDisplayNameRef.current ?? t('branchSelector.placeholder'))
      : t('branchSelector.placeholder'));

  // URL ⟷ context reconciliation, URL first. Whenever ?zone= carries a
  // resolvable value («all» or a known id) that differs from context, the
  // CONTEXT follows the URL — this is what makes browser Back/Forward work
  // across the all-branches ⇄ drill-down transition (two one-way sync effects
  // used to rewrite the restored URL right after popstate, so Back appeared
  // broken). Only when the URL has no usable zone does context write itself
  // out (initial load, internal navigation that dropped the param).
  useEffect(() => {
    const urlZoneId = searchParams.get('zone');
    if (urlZoneId === ALL_BRANCHES_PARAM) {
      if (!allBranches || branchGroupId) setAllBranches();
      return;
    }
    const gid = parseGroupParam(urlZoneId);
    if (gid) {
      if (gid !== branchGroupId) setBranchGroup(gid);
      // старый адрес «group:N» — переписать на «group-N»
      if (!urlZoneId!.startsWith(GROUP_PARAM_PREFIX)) setSearchParams(prev => { prev.set('zone', `${GROUP_PARAM_PREFIX}${gid}`); return prev; }, { replace: true });
      return;
    }
    if (urlZoneId) {
      if (!zones?.length) return; // can't validate the deep link yet — don't clobber it
      if (zones.find(z => String(z.id) === urlZoneId)) {
        if (urlZoneId !== selectedZoneId) setZone(urlZoneId);
        return;
      }
      // Unknown id falls through: context (if any) reclaims the URL, otherwise
      // the id stays for the page-level access gates (dashboard 404) to judge.
    }
    const want = branchGroupId ? `${GROUP_PARAM_PREFIX}${branchGroupId}` : allBranches ? ALL_BRANCHES_PARAM : selectedZoneId;
    if (want && urlZoneId !== want) {
      setSearchParams(prev => {
        prev.set('zone', want);
        return prev;
      }, { replace: true });
    }
  }, [searchParams, zones, selectedZoneId, allBranches, branchGroupId, setZone, setAllBranches, setBranchGroup, setSearchParams]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Position menu under trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 280) });
  }, [open]);

  // Высота меню — по ВИДИМОЙ части экрана, а не по 60vh. На iPhone (особенно в
  // приложении с домашнего экрана) клавиатура закрывает низ, а vh её не
  // учитывает: меню уходило под клавиатуру, видно было ~15 точек из 33, и
  // дальше не пролистать — приходилось печатать «pitstop» (Эдем, 14.09).
  // visualViewport сжимается вместе с клавиатурой, по нему и режем.
  const [menuMaxH, setMenuMaxH] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const top = trigger.getBoundingClientRect().bottom + 4;
      const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      setMenuMaxH(Math.max(160, Math.min(window.innerHeight * 0.6, visibleBottom - top - 8)));
    };
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, [open]);

  // Сенсорный экран: курсор в поиск НЕ ставим сразу. autoFocus на телефоне
  // открывает клавиатуру, она съедает полэкрана, и список из 33 точек
  // превращается в щель. Кто хочет искать — тапнет в поле сам. На десктопе
  // фокус остаётся: там по списку ходят стрелками.
  const coarsePointer = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true,
    [],
  );

  const syncZoneParam = useCallback((zoneId: string) => {
    setSearchParams(prev => {
      prev.set('zone', zoneId);
      prev.delete('camera');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  // Auto-select when user has exactly one visible zone.
  useEffect(() => {
    if (visibleZones.length !== 1 || selectedZoneId || allBranches) return;
    setZone(String(visibleZones[0].id));
  }, [visibleZones, selectedZoneId, allBranches, setZone]);

  const handlePick = (z: Zone) => {
    const zoneId = String(z.id);
    setZone(zoneId);
    setCamera('');
    setSelectedCamera(null);
    setFrame(null);
    syncZoneParam(zoneId);
    setOpen(false);
    setQuery('');
    onPicked?.();
  };

  // «Все филиалы»: portfolio mode. The trigger falls back to the standard
  // placeholder (no zone is selected), per-branch camera state is cleared.
  const handlePickAll = () => {
    setAllBranches();
    setCamera('');
    setSelectedCamera(null);
    setFrame(null);
    setSearchParams(prev => {
      prev.set('zone', ALL_BRANCHES_PARAM);
      prev.delete('camera');
      return prev;
    }, { replace: true });
    setOpen(false);
    setQuery('');
    onPicked?.();
  };

  const handlePickGroup = (g: MyBranchGroup) => {
    setBranchGroup(String(g.id));
    setCamera('');
    setSelectedCamera(null);
    setFrame(null);
    setSearchParams(prev => {
      prev.set('zone', `${GROUP_PARAM_PREFIX}${g.id}`);
      prev.delete('camera');
      return prev;
    }, { replace: true });
    setOpen(false);
    setQuery('');
    onPicked?.();
  };
  const filteredGroups = showGroups
    ? groups.filter(g => nameMatches(g.name, query))
    : [];

  const filtered = visibleZones.filter(z => nameMatches(z.humanreadable_name || '', query));

  // Reset highlight when the visible list shrinks/changes; clamp to bounds.
  useEffect(() => {
    setHlIndex(i => (filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => { setHlIndex(0); }, [query, open]);

  // Keep the highlighted row in view as ArrowDown walks past the fold.
  useEffect(() => {
    if (!open) return;
    const el = itemRefs.current[hlIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [hlIndex, open]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHlIndex(i => (filtered.length === 0 ? 0 : Math.min(i + 1, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHlIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      handlePick(filtered[Math.min(hlIndex, filtered.length - 1)]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const block = variant === 'block';
  const baseClass = cn(
    block
      ? 'flex min-h-14 w-full min-w-0 items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 text-left text-sm shadow-sm'
      : 'inline-flex h-9 min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm shadow-sm',
    className
  );
  const blockIcon = (
    <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
      <Building2 className="size-4" />
    </span>
  );
  const blockLabel = (name: React.ReactNode, muted = false) => (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('layout.branch')}</span>
      <span className={cn('truncate font-semibold', muted && 'text-muted-foreground')}>{name}</span>
    </span>
  );

  if (isLoading) {
    return (
      <div className={baseClass}>
        <Building2 className="size-4 text-primary shrink-0" />
        <div className="h-3.5 w-24 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  // Load FAILED (network error / timeout / non-2xx that the auth interceptor
  // didn't resolve — e.g. a slow/lossy VPN stalling the /core/api/zones request).
  // This must NOT read as "нет доступа": a failed fetch leaves `zones` undefined,
  // which is indistinguishable from a genuinely-empty membership list unless we
  // check `error` FIRST. Show a compact, honest status inline in the selector —
  // no retry button (the shared ZONES_SWR_KEY is auto-revalidated: useCurrentZone
  // polls it every 15s until a live response lands, plus SWR's own error backoff),
  // so it self-heals once the slow VPN request finally succeeds. Skipped when we
  // have data to show (cached list / already-selected zone) — SWR keeps the last
  // `data` across a failed background revalidation.
  if (error && !zones?.length && !selectedZoneId) {
    return (
      <Tooltip content={t('branchSelector.loadErrorHint')}><div className={cn(baseClass, 'text-destructive')}>
        <AlertTriangle className="size-4 shrink-0" />
        <span className="truncate">{t('branchSelector.loadError')}</span>
      </div></Tooltip>
    );
  }

  // Loaded OK but empty (HTTP 200 with an empty zones array, no error): the user
  // genuinely has no branch memberships. Only show "нет доступа" when there really
  // are no zones AND no zone is already selected. When selectedZoneId is set, zones
  // may be briefly empty during an SWR background revalidation — passing through
  // lets the cached trigger label stay visible instead of collapsing the selector
  // to the "no access" static div.
  if (!zones?.length && !selectedZoneId) {
    return (
      <div className={cn(baseClass, 'text-muted-foreground')}>
        <Building2 className="size-4 shrink-0" />
        <span className="truncate">{t('branchSelector.noAccess')}</span>
      </div>
    );
  }

  if (visibleZones.length === 1) {
    if (block) {
      return (
        <div className={baseClass}>
          {blockIcon}
          {blockLabel(selectedZone?.humanreadable_name ?? visibleZones[0].humanreadable_name)}
        </div>
      );
    }
    return (
      <div className={baseClass}>
        <Building2 className="size-4 text-primary shrink-0" />
        <span className="truncate font-medium">
          {selectedZone?.humanreadable_name ?? visibleZones[0].humanreadable_name}
        </span>
      </div>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          block
            ? 'flex min-h-14 w-full min-w-0 items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 text-left text-sm shadow-sm transition-colors'
            : 'inline-flex h-9 min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm shadow-sm transition-colors',
          'hover:border-primary/40 hover:bg-card hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          open && 'border-primary/50 ring-2 ring-ring/30',
          className
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {block ? (
          <>
            {blockIcon}
            {blockLabel(
              selectedZone?.is_archived && !allBranches
                ? <span className="flex min-w-0 items-center gap-1.5"><span className="truncate">{triggerLabel}</span><ArchivedBadge /></span>
                : triggerLabel,
              !selectedZoneId && !allBranches,
            )}
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </>
        ) : (
          <>
            <Building2 className="size-4 text-primary shrink-0" />
            <span className={cn('truncate font-medium', !selectedZoneId && !allBranches && 'text-muted-foreground')}>
              {triggerLabel}
            </span>
            {selectedZone?.is_archived && !allBranches && <ArchivedBadge />}
            <ChevronDown className="size-4 text-muted-foreground shrink-0 ml-auto" />
          </>
        )}
      </button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width,
            maxHeight: menuMaxH ?? undefined,
          }}
          className="z-[100] flex max-h-[60vh] flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg animate-in fade-in zoom-in-95 duration-100"
        >
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                autoFocus={!coarsePointer}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('branchSelector.search')}
                className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
              />
            </div>
          </div>

          {/* min-h-0 — чтобы список сжимался в колонке, а не выталкивал низ
              меню; overscroll-contain и pan-y — чтобы свайп по списку крутил
              СПИСОК, а не страницу под ним (на iOS прокрутка «проваливалась»). */}
          <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain p-1">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" /> {t('common.loading')}
              </div>
            )}
            {!isLoading && filteredGroups.length > 0 && (
              <>
                <div className="px-2 pb-0.5 pt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{t('branchSelector.groups')}</div>
                {filteredGroups.map(g => {
                  const isActive = String(g.id) === branchGroupId;
                  return (
                    <button
                      key={`g${g.id}`}
                      type="button"
                      onClick={() => handlePickGroup(g)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                        isActive ? 'bg-primary-muted text-primary-muted-foreground' : 'hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Layers className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{g.name}</span>
                      <span className="text-xs text-muted-foreground">{g.zone_ids.length}</span>
                      {isActive && <Check className="size-4 shrink-0" />}
                    </button>
                  );
                })}
                <div className="px-2 pb-0.5 pt-2 text-[11px] uppercase tracking-wider text-muted-foreground">{t('branchSelector.branches')}</div>
              </>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {query ? t('branchSelector.notFound') : t('branchSelector.empty')}
              </div>
            )}
            {!isLoading && filtered.map((z, idx) => {
              const id = String(z.id);
              const isActive = id === selectedZoneId;
              const isHighlighted = idx === hlIndex;
              return (
                <button
                  key={id}
                  ref={el => { itemRefs.current[idx] = el; }}
                  type="button"
                  onClick={() => handlePick(z)}
                  onMouseEnter={() => setHlIndex(idx)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    isActive
                      ? 'bg-primary-muted text-primary-muted-foreground'
                      : isHighlighted
                        ? 'bg-muted text-foreground'
                        : 'hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span className="flex-1 truncate">{z.humanreadable_name}</span>
                  {z.is_archived && <ArchivedBadge />}
                  {isActive && <Check className="size-4 shrink-0" />}
                </button>
              );
            })}
            {/* «Показать архивные/служебные» — в самом низу прокручиваемого списка, а не закреплены
                под ним (Алексей 25.10 #2316): нужны редко, место внизу занимать не должны. */}
            {!isLoading && (!query || filtered.length === 0) && (hasArchived && !onlyArchived || isSuperuser) && (
              <div className="mt-1 flex flex-col gap-0.5 border-t border-border/60 pt-1">
                {hasArchived && !onlyArchived && (
                  <button
                    type="button"
                    onClick={() => setShowArchived(s => !s)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {showArchived ? t('branchSelector.hideArchived') : t('branchSelector.showArchived')}
                  </button>
                )}
                {isSuperuser && (
                  <button
                    type="button"
                    onClick={() => setShowService(s => !s)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {showService ? t('branchSelector.hideService') : t('branchSelector.showService')}
                  </button>
                )}
              </div>
            )}
          </div>

          {!query && showAllBranches && <div className="border-t border-border p-1 flex flex-col gap-0.5">
              {/* «Все филиалы» — a regular-looking menu item (not a button), pinned
                  above the create-branch action per the product spec. */}
              {!query && showAllBranches && (
                <button
                  type="button"
                  onClick={handlePickAll}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    allBranches && !branchGroupId
                      ? 'bg-primary-muted text-primary-muted-foreground'
                      : 'hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span className="flex-1 truncate">{t('branchSelector.allBranches')}</span>
                  {allBranches && !branchGroupId && <Check className="size-4 shrink-0" />}
                </button>
              )}
          </div>}
        </div>,
        document.body
      )}
    </>
  );
}
